<template>
  <LISASelect
    v-if="isLISA3"
    v-bind="{
      ...$attrs,
      ...$props,
      'onUpdate:modelValue': undefined,
    }"
    v-model="modelValueAsAny"
    v-model:term="searchTerm"
    :options="options"
    :multiple="multiple"
    :selection-display="multiple ? 'chips' : 'text'"
    :placeholder="placeholder"
    :empty-actions="emptyActions"
  >
    <template v-if="$slots.prepend != null" #prepend>
      <slot name="prepend" />
    </template>
    <template v-if="$slots.append != null" #append>
      <slot name="append" />
    </template>
  </LISASelect>
  <div
    v-else
    class="flex justify-content-center"
    :class="{ 'p-inputwrapper-filled': placeholder }"
    :style="{
      minWidth: noMinWith ? undefined : '20rem',
    }"
  >
    <MultiSelect
      v-if="multiple === true"
      v-model="modelValueAsAny"
      :options="options"
      :show-clear="!required"
      :loading="loading"
      :disabled="disabled"
      :link-to-origin="config.linkToOrigin"
      :allow-create="allowCreate"
      :placeholder="placeholder || undefined"
      :data-testid="dataTestid"
      :state="state"
      :close-on-select="closeOnSelect"
      :group-selected-options="groupSelectedOptions"
      :inline="inline"
      :option-states="optionStates"
      :value-overlay-label="valueOverlayLabel"
      :hide-jump-to-origin="
        !isSpecialAdmin() || hideJumpToOrigin || blockJumpToOrigin || hasOverlaySelected
      "
      @create="openCreate"
      @open="$emit('open')"
      @close="$emit('close')"
    >
      <template v-if="$slots['prepend'] != null" #prepend>
        <slot name="prepend" />
      </template>
    </MultiSelect>
    <SingleSelect
      v-else
      v-model="modelValueAsAny"
      :options="options"
      :show-clear="!required"
      :loading="loading"
      :disabled="disabled"
      :link-to-origin="config.linkToOrigin"
      :allow-create="allowCreate"
      :data-testid="dataTestid"
      :placeholder="placeholder || undefined"
      :state="state"
      :value-overlay-label="valueOverlayLabel"
      :hide-jump-to-origin="
        !isSpecialAdmin() || hideJumpToOrigin || blockJumpToOrigin || hasOverlaySelected
      "
      @create="openCreate"
      @open="$emit('open')"
      @close="$emit('close')"
    >
      <template v-if="$slots['prepend'] != null" #prepend>
        <slot name="prepend" />
      </template>
    </SingleSelect>
  </div>
  <component
    :is="config.createComponent"
    v-bind="additionalCreateParameter"
    v-if="showCreate && config.createComponent"
    :show-in-modal="true"
    :visible="true"
    :default="defaultCreate"
    @created="addCreatedElement"
    @cancel="showCreate = false"
    @abort="showCreate = false"
  />
</template>

<script setup lang="ts" generic="V extends boolean, Z extends SelectMode">
import MultiSelect from "./base/MultiSelect.vue";
import SingleSelect from "./base/SingleSelect.vue";
import { computed, watch } from "vue";
import { mapUserToSelectOption, SelectMode, SelectModeMap } from "./SelectDefinitions";
import type { MapResultEntry, ModeVariables, MultiSelectOptionState } from "./SelectHelper";
import { ref } from "vue";
import { useTranslation } from "@/language/translation";
import { useRouter } from "vue-router";
import { routerRightCheck, type RouterAccessRightMetaType } from "@/router";
import { isSpecialAdmin } from "@/functions/environment";
import {
  SQDCUserFieldSelectionMode,
  UserFunctionScope,
  useSearchUsersQuery,
  useUserFunctionsQuery,
  type SearchUsersQueryVariables,
} from "@/graphql";
import store from "@/store";
import { getUserFunctionAssignment, toUserFunctionSelectValue } from "./UserFunctionSelectHelper";
import { LISASelect, type LISAEmptyAction } from "@fi-solutions/lisa-ui";
import { isLISA3 } from "@/composables/useLISA3.ts";

type ModelValue<V extends boolean> = [V] extends [true] ? number[] : number;

const props = withDefaults(
  defineProps<{
    multiple?: V;
    required?: boolean;
    filters?: ModeVariables<Z>;
    mode: Z;
    disabled?: boolean;
    dataTestid?: string;
    autoPickFirst?: boolean;
    exclude?: number[];
    additionalOptions?: MapResultEntry[];
    additionalOptionsPosition?: "start" | "end";
    placeholder?: string;
    state?: boolean | null;
    valueOverlayLabel?: string;
    hideJumpToOrigin?: boolean;
    additionalCreateParameter?: Record<string, any>;
    floatingLabel?: boolean;
    noMinWith?: boolean;
    userSelectionMode?: SQDCUserFieldSelectionMode;
    userFunctionSiteId?: number;
    closeOnSelect?: boolean;
    groupSelectedOptions?: boolean;
    inline?: boolean;
    optionStates?: Record<number, MultiSelectOptionState>;
  }>(),
  {
    multiple: undefined,
    filters: undefined,
    dataTestid: undefined,
    exclude: undefined,
    additionalOptions: undefined,
    additionalOptionsPosition: "end",
    placeholder: undefined,
    state: undefined,
    valueOverlayLabel: undefined,
    additionalCreateParameter: undefined,
    userSelectionMode: SQDCUserFieldSelectionMode.USER,
    userFunctionSiteId: undefined,
    closeOnSelect: false,
    groupSelectedOptions: true,
    inline: false,
    optionStates: undefined,
  },
);

const modelValue = defineModel<ModelValue<V>>();
defineEmits<{
  open: [];
  close: [];
}>();
const modelValueAsAny = modelValue as any;

const config = computed(() => SelectModeMap[props.mode]);
const allowCreate = computed(
  () =>
    config.value.createComponent != null &&
    !(
      props.mode === SelectMode.USER &&
      props.userSelectionMode === SQDCUserFieldSelectionMode.FUNCTION
    ),
);

// @ts-expect-error The union of generated query composables is too complex for TypeScript.
const queryResult = config.value.useQuery(
  // @ts-expect-error The union of generated filter functions is too complex for TypeScript.
  computed(() => config.value.specialFilters?.(props.filters ?? {}) ?? props.filters ?? {}) as any,
);
const { result, loading: optionsLoading, refetch } = queryResult;

const selectedIds = computed<number[]>(() => {
  if (modelValue.value == null) {
    return [];
  }
  return Array.isArray(modelValue.value)
    ? (modelValue.value as number[])
    : [modelValue.value as number];
});
const hasOverlaySelected = computed(() => selectedIds.value.some((id) => id < 0));

const currentUserId = computed<number | undefined>(() => {
  const id = store.getters["login/userId"];
  return typeof id === "number" ? id : undefined;
});

const exceptionalUserIds = computed(() => {
  if (props.mode !== SelectMode.USER) {
    return [];
  }
  return [...new Set([...selectedIds.value, currentUserId.value])].filter(
    (id): id is number => id != null && id > 0,
  );
});

const exceptionalUserVariables = computed<SearchUsersQueryVariables>(() => {
  const filters =
    props.mode === SelectMode.USER
      ? (props.filters as SearchUsersQueryVariables | undefined)
      : undefined;
  return {
    siteIds: filters?.siteIds,
    hasRightsOnSite: filters?.hasRightsOnSite,
    ids: exceptionalUserIds.value,
    // The Users resolver requires true here to include hidden users.
    hide: true,
  };
});

const { result: exceptionalUsersResult, loading: exceptionalUsersLoading } = useSearchUsersQuery(
  exceptionalUserVariables,
  () => ({
    enabled: props.mode === SelectMode.USER && exceptionalUserIds.value.length > 0,
  }),
);

const functionsEnabled = computed(
  () =>
    props.mode === SelectMode.USER && props.userSelectionMode !== SQDCUserFieldSelectionMode.USER,
);
const { result: userFunctionsResult, loading: userFunctionsLoading } = useUserFunctionsQuery(
  computed(() => ({
    scopes: [UserFunctionScope.SITE, UserFunctionScope.GLOBAL],
    siteIds: props.userFunctionSiteId == null ? undefined : [props.userFunctionSiteId],
  })),
  () => ({ enabled: functionsEnabled.value, fetchPolicy: "cache-first" }),
);

const loading = computed(
  () =>
    optionsLoading.value ||
    exceptionalUsersLoading.value ||
    (functionsEnabled.value && userFunctionsLoading.value),
);

const { t, te } = useTranslation();

const options = computed(() => {
  const mappedOptions = config.value
    .mapResult(result as any, t, te)
    .filter((option) => props.exclude == null || !props.exclude.includes(option.id));
  const res =
    props.mode === SelectMode.USER &&
    props.userSelectionMode === SQDCUserFieldSelectionMode.FUNCTION
      ? []
      : mappedOptions;

  if (functionsEnabled.value) {
    for (const userFunction of userFunctionsResult.value?.UserFunctions ?? []) {
      const id = toUserFunctionSelectValue(userFunction.id);
      const hasAssignment =
        getUserFunctionAssignment(userFunction, props.userFunctionSiteId) != null;
      if (!hasAssignment && !selectedIds.value.includes(id)) continue;
      if (props.exclude?.includes(id) || res.some((option) => option.id === id)) continue;
      res.push({
        id,
        label: String(t(`dynamic.${userFunction.nameId}`)),
        disabled: !hasAssignment,
      });
    }
  }

  const exceptionalUsers = new Map(
    exceptionalUsersResult.value?.Users.map((user) => {
      const option = mapUserToSelectOption(user);
      return [option.id, option] as const;
    }) ?? [],
  );

  if (
    props.mode === SelectMode.USER &&
    props.userSelectionMode !== SQDCUserFieldSelectionMode.FUNCTION &&
    currentUserId.value != null
  ) {
    const configuredIds = (props.filters as SearchUsersQueryVariables | undefined)?.ids;
    const allowedIds =
      configuredIds == null
        ? undefined
        : Array.isArray(configuredIds)
          ? configuredIds
          : [configuredIds];
    const currentUserIsAllowed =
      (allowedIds == null || allowedIds.includes(currentUserId.value)) &&
      (props.exclude == null || !props.exclude.includes(currentUserId.value));
    const currentUser = exceptionalUsers.get(currentUserId.value);
    if (
      currentUserIsAllowed &&
      currentUser != null &&
      !res.some((option) => option.id === currentUser.id)
    ) {
      res.unshift(currentUser);
    }
  }

  const additionalOptions = (props.additionalOptions ?? []).filter(
    (option) => !res.some((el) => el.id === option.id),
  );
  if (props.additionalOptionsPosition === "start") res.unshift(...additionalOptions);
  else res.push(...additionalOptions);

  if (selectedIds.value.length > 0) {
    const missing = selectedIds.value.filter((id) => !res.some((option) => option.id === id));
    for (const id of missing) {
      const selectedUser = exceptionalUsers.get(id);
      res.unshift({
        id: id,
        label: selectedUser?.label ?? `${t("global.deleted")} | ID: ${id}`,
        disabled: true,
      });
    }
  }

  return res;
});

watch(
  options,
  () => {
    if (
      modelValue.value == null ||
      (Array.isArray(modelValue.value) && modelValue.value.length === 0)
    ) {
      if (props.autoPickFirst && options.value.length > 0) {
        if (props.multiple === true) {
          modelValue.value = [options.value[0].id] as ModelValue<V>;
        } else {
          modelValue.value = options.value[0].id as ModelValue<V>;
        }
      }
    }
  },
  { immediate: true },
);

if (import.meta.env.DEV) {
  watch(
    () => props.mode,
    () => {
      throw new Error("Changing mode at runtime is not supported.");
    },
  );
}

const router = useRouter();

const blockJumpToOrigin = computed(() => {
  if (config.value.linkToOrigin?.name != null) {
    const route = router.getRoutes().find((r) => r.name === config.value.linkToOrigin?.name);
    return !routerRightCheck(route?.meta as RouterAccessRightMetaType | undefined);
  }
  return false;
});

const showCreate = ref(false);
const defaultCreate = ref({});
function openCreate(searchString: string) {
  showCreate.value = true;
  defaultCreate.value = config.value.getDefaultCreate
    ? config.value.getDefaultCreate(searchString)
    : { name: searchString };
}

async function addCreatedElement(element: { id: number }) {
  await refetch();
  if (props.multiple) {
    modelValue.value = [
      ...((modelValue.value ?? []) as ModelValue<V>[]),
      element.id,
    ] as ModelValue<V>;
  } else {
    modelValue.value = element.id as ModelValue<V>;
  }
  showCreate.value = false;
}

const searchTerm = ref<string>();

const emptyActions = computed<LISAEmptyAction[] | undefined>(() => {
  if (allowCreate.value === false) return undefined;
  if (searchTerm.value == null) return undefined;
  if (searchTerm.value === "") return undefined;

  return [
    {
      id: "term",
      icon: "fas fa-plus",
      variant: "info",
      label: t("components.lSelect.wantToCreate", {
        searchString: searchTerm.value,
      }),
      callback: () => {
        openCreate(searchTerm.value!);
      },
    },
  ];
});
</script>

<style lang="scss" scoped></style>
